import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Pencil, ShieldCheck, ShieldOff, UserX, UserCheck } from 'lucide-react';
import { useUsers, useDeleteUser, useUpdateUser, useSession } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';

function accessLabel(u: {
  role: 'admin' | 'user';
  allowedFonti: string[] | null;
  dataFrom: string | null;
  dataTo: string | null;
}): string {
  if (u.role === 'admin') return 'Tutto';
  const fonti = u.allowedFonti?.length ? u.allowedFonti.join(', ') : 'nessuna fonte';
  const periodo =
    u.dataFrom || u.dataTo ? ` · ${u.dataFrom ?? '…'} → ${u.dataTo ?? '…'}` : '';
  return `${fonti}${periodo}`;
}

export function AdminUsersPage() {
  const users = useUsers();
  const del = useDeleteUser();
  const update = useUpdateUser();
  const { data: session } = useSession();
  const myId = session?.user.id;
  const [pendingDelete, setPendingDelete] = useState<{ id: string; username: string } | null>(null);

  async function toggleRole(id: string, currentRole: 'admin' | 'user') {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    await update.mutateAsync({ id, input: { role: newRole } });
  }

  async function toggleBan(id: string, banned: boolean) {
    await update.mutateAsync({ id, input: { banned: !banned, banReason: banned ? null : 'Sospeso da admin' } });
  }

  return (
    <div className="container-wide py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl font-semibold">Utenti</h1>
        <Button asChild>
          <Link to="/admin/utenti/nuovo">
            <Plus className="h-4 w-4" /> Nuovo utente
          </Link>
        </Button>
      </div>

      {users.isLoading ? (
        <p className="text-sm text-(--color-muted-foreground)">Caricamento…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-(--color-muted-foreground) border-b border-(--color-border)">
              <tr>
                <th className="text-left py-2 pr-3">Username</th>
                <th className="text-left py-2 pr-3">Ruolo</th>
                <th className="text-left py-2 pr-3">Accesso</th>
                <th className="text-left py-2 pr-3">Stato</th>
                <th className="text-right py-2 w-48">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.users.map((u) => {
                const isMe = u.id === myId;
                return (
                  <tr key={u.id} className="border-b border-(--color-border)">
                    <td className="py-2 pr-3 font-medium">{u.username ?? '—'}</td>
                    <td className="py-2 pr-3">
                      <Badge variant={u.role === 'admin' ? 'default' : 'outline'}>
                        {u.role === 'admin' ? 'admin' : 'lettore'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 max-w-xs truncate text-(--color-muted-foreground)">
                      {accessLabel(u)}
                    </td>
                    <td className="py-2 pr-3">
                      {u.banned ? (
                        <Badge variant="destructive">sospeso</Badge>
                      ) : (
                        <Badge variant="secondary">attivo</Badge>
                      )}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Modifica" asChild>
                            <Link to={`/admin/utenti/${u.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Modifica</TooltipContent>
                      </Tooltip>
                      {!isMe && (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={u.role === 'admin' ? 'Rimuovi admin' : 'Promuovi admin'}
                                onClick={() => toggleRole(u.id, u.role)}
                              >
                                {u.role === 'admin' ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {u.role === 'admin' ? 'Rimuovi admin' : 'Promuovi admin'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={u.banned ? 'Riattiva' : 'Sospendi'}
                                onClick={() => toggleBan(u.id, u.banned)}
                              >
                                {u.banned ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{u.banned ? 'Riattiva' : 'Sospendi'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Elimina"
                                onClick={() => setPendingDelete({ id: u.id, username: u.username ?? u.id })}
                              >
                                <Trash2 className="h-4 w-4 text-(--color-destructive)" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Elimina</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                      {isMe && <span className="ml-1 text-xs text-(--color-muted-foreground)">(tu)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        itemLabel={pendingDelete ? `l'utente ${pendingDelete.username}` : ''}
        isPending={del.isPending}
        onConfirm={async () => {
          if (pendingDelete) await del.mutateAsync(pendingDelete.id);
        }}
      />
    </div>
  );
}
